import React from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { DollarSign, TrendingUp, Percent, Landmark, Wallet, Users, Zap, ArrowUpRight, UserMinus } from "lucide-react";

const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatCard = ({ label, value, sub, icon: Icon, tint, testId, warn }) => (
  <Card className="border-border bg-card overflow-hidden relative">
    <div className={`absolute inset-0 bg-gradient-to-br ${tint} to-transparent`} />
    <CardContent className="p-4 relative">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-foreground mt-1 tabular-nums truncate" data-testid={testId}>{value}</p>
          {sub && <p className={`text-[11px] mt-1 ${warn ? "text-amber-400 font-medium" : "text-muted-foreground"}`}>{sub}</p>}
        </div>
        <div className="w-9 h-9 rounded-lg bg-muted/60 flex items-center justify-center shrink-0">
          <Icon className="w-4.5 h-4.5 text-muted-foreground" style={{ width: 18, height: 18 }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const RevenueTopCards = ({ stats }) => (
  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="revenue-summary-cards">
    <StatCard label="Subscription Revenue" value={fmt(stats.subscription_revenue)} icon={DollarSign} tint="from-emerald-500/5"
      sub={`All-time: ${fmt(stats.all_time?.subscription_revenue)}`} testId="subscription-revenue-value" />
    <StatCard label="Fee Revenue" value={fmt(stats.fee_revenue)} icon={Percent} tint="from-cyan-500/5"
      sub={`3.8% + $0.30 per shop payment · all-time ${fmt(stats.all_time?.fee_revenue)}`} testId="fee-revenue-value" />
    <StatCard label="Total Fieldz Revenue" value={fmt(stats.total_fieldz_revenue)} icon={Landmark} tint="from-blue-500/5"
      sub={`Subscriptions + fees${stats.topup_revenue ? ` + ${fmt(stats.topup_revenue)} top-ups` : ""}`} testId="total-fieldz-revenue-value" />
    <StatCard label="MRR" value={fmt(stats.mrr)} icon={TrendingUp} tint="from-violet-500/5"
      sub={`${stats.paying_customers || 0} paying customers`} testId="mrr-value" />
    <StatCard label="Payment Volume Processed" value={fmt(stats.payment_volume)} icon={Wallet} tint="from-amber-500/5"
      sub="Shop-collected — NOT Fieldz revenue" warn testId="payment-volume-value" />
  </div>
);

export const InvestorCards = ({ stats }) => (
  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="investor-cards">
    <StatCard label="Avg Revenue / Customer" value={fmt(stats.arpu)} icon={Users} tint="from-teal-500/5"
      sub="MRR ÷ paying customers" testId="arpu-value" />
    <StatCard label="Monthly Churn Rate" value={`${stats.churn_rate || 0}%`} icon={UserMinus} tint="from-red-500/5"
      sub={`${stats.churned_this_month || 0} churned this month`} testId="churn-rate-value" />
    <StatCard label="Net New MRR (month)" value={fmt(stats.net_new_mrr)} icon={ArrowUpRight} tint="from-emerald-500/5"
      sub="New MRR − churned MRR" testId="net-new-mrr-value" />
    <StatCard label="Checkout Completion" value={`${stats.checkout_completion_rate || 0}%`} icon={Zap} tint="from-blue-500/5"
      sub={`${stats.unique_sessions_paid || 0} of ${stats.unique_sessions || 0} unique sessions`} testId="checkout-completion-value" />
    <StatCard label="Trial → Paid" value={`${stats.trial_to_paid?.rate || 0}%`} icon={TrendingUp} tint="from-violet-500/5"
      sub={`${stats.trial_to_paid?.converted || 0} of ${stats.trial_to_paid?.trial_owners || 0} trial owners`} testId="trial-to-paid-value" />
  </div>
);
